import PropTypes from 'prop-types';
import { Form } from 'formik';
import * as Yup from 'yup';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import MLink from '@material-ui/core/Link';
import TextField from '../forms/text-field';
import FormSubmitStatus from '../forms/form-submit-status';
import FormHost from '../events/form-host';
import FormGroup from '../events/form-group';
import FormConvertEvents from './form-convert-events';
import TextAreaField from '../forms/text-area-field';
import MaterialLink from '../material-link';

const useStyles = makeStyles((theme) => ({
  submitButtons: {
    marginTop: theme.spacing(2),
  },
  migrateHeader: {
    marginBottom: theme.spacing(1),
  },
}));

export const newEventSeriesSchema = Yup.object().shape({
  hostType: Yup.string()
    .oneOf(['User', 'Group'])
    .required(),
  groupId: Yup.string().nullable(),
  name: Yup.string().nullable().required('Name is required'),
  description: Yup.string().nullable(),
  convertEventIds: Yup.array()
    .min(1, 'At least 1 existing event is required to create series')
    .required('Series must be converted from an existing event')
}).test('host-group-selected', function testHostGroupSelected(values) {
  const { hostType, groupId } = values;
  if (hostType === 'Group' && !groupId) {
    return this.createError({
      path: 'groupId',
      message: 'Group must be selected if host is a group.',
    });
  }
});

const NewEventSeriesForm = (props) => {
  const {
    isSubmitting,
    submitCount,
    disabled: formDisabled,
  } = props;

  const classes = useStyles();
  const disabled = formDisabled || isSubmitting;
  const forceTouched = submitCount > 0;

  return (
    <Form>
      <FormGroup
        disabled={disabled}
        forceTouched={forceTouched}
      />
      <FormHost
        disabled={disabled}
        forceTouched={forceTouched}
      />
      <TextField
        name="name"
        label="Series Name"
        disabled={disabled}
        touched={forceTouched}
      />
      <TextAreaField
        name="description"
        label="Series Description (optional)"
        disabled={disabled}
        touched={forceTouched}
        helperText={(
          <span>
            <MLink href="https://www.markdownguide.org/basic-syntax/" target="_blank">
              Markdown
            </MLink>
            {' '}is supported
          </span>
        )}
      />
      
      <Typography variant="h2" className={classes.migrateHeader}>
        Migrate Existing Events into Series
      </Typography>
      <p>To create a series, you need to start with an existing or <MaterialLink href="events/new">new event</MaterialLink> which will serve as the template for future events you schedule. They only thing that will change to events you convert is they&lsquo;ll now be linked to this series.</p>
      <FormConvertEvents
        disabled={disabled}
        touched={forceTouched}
      />

      <Typography variant="h2" className={classes.migrateHeader}>
        Add New Events to Series
      </Typography>
      <p>Set schedule and add new events on the next page after creating.</p>

      <div className={classes.submitButtons}>
        <Button
          type="submit"
          disabled={disabled}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
        <FormSubmitStatus {...props} />
      </div>
    </Form>
  );
};

NewEventSeriesForm.propTypes = {
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  name: PropTypes.string,
  submitCount: PropTypes.number,
};

export default NewEventSeriesForm;
